import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSession } from "@/services/auth0/session";
import { useEffect, useMemo, useState } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
// import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, PlusCircle, Loader2 } from "lucide-react";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";
import { ShipmentMap } from "./shipment-map";
import { CreateOrderSchema, createOrderSchema } from "@/api.schemas";
import { useCreateOrder } from "@/api/use-order";
import { usePricingOptions } from "@/api/use-order-pricing-options";
import { DockedContainer } from "@/components/docked-container";
import { useCustomerAddresses } from "@/api/use-customer-addresses";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { logger } from "@/services/logger";
import { AddAddressDialog } from "./add-address-dialog";

interface CreateOrderFormProps {
  initialData?: CreateOrderSchema;
  initialAddresses?: {
    pickup: string;
    delivery: string;
  };
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export function CreateOrderForm({
  initialData,
  initialAddresses,
  onSuccess,
  onError,
}: CreateOrderFormProps) {
  const { customer } = useSession();
  const { addresses } = useCustomerAddresses();
  const [useSavedAddress, setUseSavedAddress] = useState(
    addresses && addresses.length > 0
  );
  const [isAddAddressDialogOpen, setIsAddAddressDialogOpen] = useState(false);
  const form = useForm<CreateOrderSchema & { selectedAddress?: string }>({
    resolver: zodResolver(createOrderSchema),
    defaultValues: initialData ?? {
      selectedAddress: "",
      type: "STANDARD",
      status: "PENDING",
      recipientName: "",
      recipientEmail: "",
      recipientPhone: "",
      senderName: "",
      senderEmail: "",
      senderPhone: "",
      customerType: "INDIVIDUAL",
      pickupNotes: "",
      deliveryNotes: "",
      pickupAddress: {
        line1: "",
        line2: "",
        city: "",
        province: "",
        postalCode: "",
        country: "",
        location: { lat: 0, lng: 0 },
      },
      deliveryAddress: {
        line1: "",
        line2: "",
        city: "",
        province: "",
        postalCode: "",
        country: "",
        location: { lat: 0, lng: 0 },
      },
      parcels: [
        {
          weight: 2,
          dimensions: { length: 30, width: 30, height: 30 },
          isFragile: false,
          requiresCooling: false,
          requiresWarm: false,
          isPriority: false,
          requireSignature: false,
          extraInsurance: false,
          leaveAtDoor: false,
          value: 0,
        },
      ],
    },
  });

  const watchedValues = useWatch({
    control: form.control,
    name: ["pickupAddress", "deliveryAddress", "parcels"],
  });

  const pricingInputs = useMemo(
    () => ({
      pickupAddress: watchedValues[0],
      deliveryAddress: watchedValues[1],
      parcels: watchedValues[2]?.map((parcel) => ({
        weight: Number(parcel.weight) || 0,
        dimensions: {
          length: Number(parcel.dimensions?.length) || 0,
          width: Number(parcel.dimensions?.width) || 0,
          height: Number(parcel.dimensions?.height) || 0,
        },
        isFragile: Boolean(parcel.isFragile),
      })),
    }),
    [watchedValues]
  );

  const {
    pricingOptions,
    pricingOptionMetadata,
    error: shippingOptionsError,
  } = usePricingOptions(pricingInputs);

  // Set first pricing option when options become available
  useEffect(() => {
    if (pricingOptions.length > 0) {
      form.setValue(
        "type",
        pricingOptions[0].speedType as
          | "STANDARD"
          | "EXPRESS"
          | "SCHEDULED"
          | "SAME_DAY"
      );
    }
  }, [pricingOptions, form]);

  const createOrderMutation = useCreateOrder({
    onSuccess: () => {
      form.reset();
      onSuccess?.();
    },
    onError,
  });

  const onSubmit = form.handleSubmit((values) => {
    try {
      const selectedOption = pricingOptions.find(
        (option) => option.speedType === values.type
      );

      if (!selectedOption) {
        logger.error("No shipping option selected");
        return;
      }

      const formattedParcels = values.parcels.map((parcel) => ({
        weight: Number(parcel.weight) || 0,
        dimensions: {
          length: Number(parcel.dimensions?.length) || 0,
          width: Number(parcel.dimensions?.width) || 0,
          height: Number(parcel.dimensions?.height) || 0,
        },
        isFragile: Boolean(parcel.isFragile),
        requiresCooling: Boolean(parcel.requiresCooling),
        requiresWarm: Boolean(parcel.requiresWarm),
        isPriority: Boolean(parcel.isPriority),
        requireSignature: Boolean(parcel.requireSignature),
        extraInsurance: Boolean(parcel.extraInsurance),
        leaveAtDoor: Boolean(parcel.leaveAtDoor),
        value: Number(parcel.value) || 0,
      }));

      const submitData = {
        ...values,
        placerCustomerId: customer?.id,
        pickupDepotId: pricingOptionMetadata?.pickupServiceArea?.depotId,
        deliveryDepotId: pricingOptionMetadata?.deliveryServiceArea?.depotId,
        senderEmail: values.senderEmail?.trim() ?? null,
        recipientEmail: values.recipientEmail?.trim() ?? null,
        parcels: formattedParcels,
      };

      createOrderMutation.mutate(submitData);
    } catch (error) {
      logger.error("Error preparing order data:", error);
    }
  });

  useEffect(() => {
    if (initialAddresses) {
      // Initialize addresses with the raw strings, AutocompleteAddress component
      // will handle the Google Places validation and parsing
      form.setValue("pickupAddress", {
        ...form.getValues("pickupAddress"),
        line1: initialAddresses.pickup,
      });
      form.setValue("deliveryAddress", {
        ...form.getValues("deliveryAddress"),
        line1: initialAddresses.delivery,
      });
    }
  }, [initialAddresses, form]);

  useEffect(() => {
    if (!useSavedAddress) {
      // Clear sender details when toggling off
      form.setValue("selectedAddress", "");
      form.setValue("senderName", "");
      form.setValue("senderPhone", "");
      form.setValue("pickupAddress", {
        line1: "",
        line2: "",
        city: "",
        province: "",
        postalCode: "",
        country: "",
        location: { lat: 0, lng: 0 },
      });
      form.setValue("pickupNotes", "");
    } else {
      // Set primary address when toggling on
      const primaryAddress = addresses?.find((addr) => addr.isPrimary);
      if (primaryAddress) {
        form.setValue("selectedAddress", primaryAddress.id);
        form.setValue("pickupNotes", primaryAddress.pickupNotes ?? "");
        form.setValue("senderName", primaryAddress.name ?? "");
        form.setValue("senderPhone", primaryAddress.phone ?? "");
        form.setValue("pickupAddress", {
          line1: primaryAddress.address.line1,
          line2: primaryAddress.address.line2 ?? "",
          city: primaryAddress.address.city,
          province: primaryAddress.address.province,
          postalCode: primaryAddress.address.postalCode ?? "",
          country: primaryAddress.address.country,
          location: primaryAddress.address.location,
        });
      }
    }
  }, [useSavedAddress, addresses, form]);

  useEffect(() => {
    const primaryAddress = addresses?.find((addr) => addr.isPrimary);
    if (primaryAddress && !initialData && useSavedAddress) {
      form.setValue("senderName", primaryAddress.name ?? "");
      form.setValue("senderPhone", primaryAddress.phone ?? "");
      form.setValue("pickupNotes", primaryAddress.pickupNotes ?? "");
      form.setValue("pickupAddress", {
        line1: primaryAddress.address.line1,
        line2: primaryAddress.address.line2 ?? "",
        city: primaryAddress.address.city,
        province: primaryAddress.address.province,
        postalCode: primaryAddress.address.postalCode ?? "",
        country: primaryAddress.address.country,
        location: primaryAddress.address.location,
      });
      form.setValue("selectedAddress", primaryAddress.id);
    }
  }, [addresses, form, initialData, useSavedAddress]);

  return (
    <div className="flex flex-col flex-1">
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col">
          <div className="flex-1 space-y-8 flex-col flex mx-auto w-full lg:max-w-7xl px-4 lg:px-8 pb-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Sender Column */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-left">Sender Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-1">
                      <FormLabel>Use Saved Address</FormLabel>
                      <span className="text-sm text-muted-foreground">
                        Switch to select from your saved addresses
                      </span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4">
                      {(!addresses || addresses.length === 0) && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsAddAddressDialogOpen(true)}
                          className="flex-1 sm:flex-none text-sm px-3 h-9"
                        >
                          Add New Address
                        </Button>
                      )}
                      <Switch
                        checked={useSavedAddress}
                        onCheckedChange={setUseSavedAddress}
                        disabled={!addresses || addresses.length === 0}
                      />
                    </div>
                  </div>

                  <AddAddressDialog
                    open={isAddAddressDialogOpen}
                    onOpenChange={setIsAddAddressDialogOpen}
                    onSuccess={() => {
                      // Find the newly added address (should be the last one)
                      const newAddress = addresses?.[addresses.length - 1];
                      if (newAddress) {
                        form.setValue("selectedAddress", newAddress.id);
                        form.setValue("senderName", newAddress.name ?? "");
                        form.setValue("senderPhone", newAddress.phone ?? "");
                        form.setValue("pickupAddress", {
                          line1: newAddress.address.line1,
                          line2: newAddress.address.line2 ?? "",
                          city: newAddress.address.city,
                          province: newAddress.address.province,
                          postalCode: newAddress.address.postalCode ?? "",
                          country: newAddress.address.country,
                          location: newAddress.address.location,
                        });
                      }
                      setUseSavedAddress(true);
                    }}
                  />

                  {useSavedAddress && (
                    <div className="w-full">
                      <Select
                        value={form.watch("selectedAddress") ?? ""}
                        onValueChange={(addressId) => {
                          const selectedAddress = addresses?.find(
                            (addr) => addr.id === addressId
                          );
                          if (selectedAddress) {
                            form.setValue("selectedAddress", addressId);
                            form.setValue(
                              "senderName",
                              selectedAddress.name ?? ""
                            );
                            form.setValue(
                              "senderPhone",
                              selectedAddress.phone ?? ""
                            );
                            form.setValue("pickupAddress", {
                              line1: selectedAddress.address.line1,
                              line2: selectedAddress.address.line2 ?? "",
                              city: selectedAddress.address.city,
                              province: selectedAddress.address.province,
                              postalCode:
                                selectedAddress.address.postalCode ?? "",
                              country: selectedAddress.address.country,
                              location: selectedAddress.address.location,
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="h-10 py-2">
                          <SelectValue placeholder="Select a saved address" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {addresses?.map((addr) => (
                            <SelectItem key={addr.id} value={addr.id}>
                              {addr.label ?? addr.address.line1}{" "}
                              {addr.isPrimary && "(Primary)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="w-full">
                    <FormLabel>Pickup Address</FormLabel>
                    <div className="mt-2 relative">
                      {useSavedAddress ? (
                        <AutocompleteAddress
                          onSelectedAddress={() => {
                            // Do nothing
                          }}
                          placeholder="Pickup address"
                          initialValue={form.watch("pickupAddress.line1")}
                          initialAddressComponents={{
                            line1: form.watch("pickupAddress.line1") ?? "",
                            line2: form.watch("pickupAddress.line2") ?? "",
                            city: form.watch("pickupAddress.city") ?? "",
                            state: form.watch("pickupAddress.province") ?? "",
                            postalCode:
                              form.watch("pickupAddress.postalCode") ?? "",
                            country: form.watch("pickupAddress.country") ?? "",
                          }}
                          readOnly={true}
                          showFullForm={true}
                        />
                      ) : (
                        <AutocompleteAddress
                          onSelectedAddress={(address) => {
                            if (address) {
                              form.setValue("pickupAddress", {
                                line1: address.addressComponents.line1,
                                line2: address.addressComponents.line2,
                                city: address.addressComponents.city,
                                province: address.addressComponents.state,
                                postalCode:
                                  address.addressComponents.postalCode || "",
                                country: address.addressComponents.country,
                                location: address.location,
                              });
                            }
                          }}
                          placeholder="Search pickup address"
                        />
                      )}
                    </div>
                  </div>

                  <div className="w-full">
                    <FormField
                      control={form.control}
                      name="pickupNotes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pickup Notes</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Add any special instructions for pickup"
                              className="h-10 py-2"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="senderName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sender Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Sender Name"
                            {...field}
                            disabled={useSavedAddress}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="senderPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sender Phone</FormLabel>
                        <FormControl>
                          <PhoneInputShadcn
                            placeholder="Sender Phone"
                            {...field}
                            value={field.value}
                            onChange={(value) => field.onChange(value ?? "")}
                            defaultCountry="CA"
                            disabled={useSavedAddress}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Recipient Column */}
              <Card>
                <CardHeader>
                  <CardTitle>Recipient Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="w-full">
                    <FormLabel>Delivery Address</FormLabel>
                    <div className="mt-2 relative">
                      <AutocompleteAddress
                        onSelectedAddress={(address) => {
                          if (address) {
                            form.setValue("deliveryAddress", {
                              line1: address.addressComponents.line1,
                              line2: address.addressComponents.line2,
                              city: address.addressComponents.city,
                              province: address.addressComponents.state,
                              postalCode: address.addressComponents.postalCode,
                              country: address.addressComponents.country,
                              location: address.location,
                            });
                          }
                        }}
                        placeholder="Search delivery address"
                      />
                    </div>
                  </div>

                  <div className="w-full">
                    <FormField
                      control={form.control}
                      name="deliveryNotes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Delivery Notes</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Add any special instructions for delivery"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="recipientName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Recipient Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Recipient Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="recipientPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Recipient Phone</FormLabel>
                        <FormControl>
                          <PhoneInputShadcn
                            placeholder="Recipient Phone"
                            {...field}
                            value={field.value}
                            onChange={(value) => field.onChange(value ?? "")}
                            defaultCountry="CA"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Parcels Card */}
            <Card>
              <CardHeader>
                <CardTitle>Parcels</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dimensions (L×W×H cm)</TableHead>
                      <TableHead>Weight (kg)</TableHead>
                      <TableHead>Special Handling</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.watch("parcels").map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Input
                              type="number"
                              placeholder="L"
                              {...form.register(
                                `parcels.${index}.dimensions.length`
                              )}
                              className="w-20"
                            />
                            <Input
                              type="number"
                              placeholder="W"
                              {...form.register(
                                `parcels.${index}.dimensions.width`
                              )}
                              className="w-20"
                            />
                            <Input
                              type="number"
                              placeholder="H"
                              {...form.register(
                                `parcels.${index}.dimensions.height`
                              )}
                              className="w-20"
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            placeholder="Weight"
                            {...form.register(`parcels.${index}.weight`)}
                            className="w-24"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            {/* 
                            <Checkbox
                              checked={form.watch(`parcels.${index}.isFragile`)}
                              onCheckedChange={(checked) =>
                                form.setValue(
                                  `parcels.${index}.isFragile`,
                                  !!checked
                                )
                              }
                            />
                            <span className="text-sm">Fragile</span>
                            <Checkbox
                              checked={form.watch(
                                `parcels.${index}.requiresCooling`
                              )}
                              onCheckedChange={(checked) =>
                                form.setValue(
                                  `parcels.${index}.requiresCooling`,
                                  !!checked
                                )
                              }
                            />
                            <span className="text-sm">Cooling</span>
                            */}
                          </div>
                        </TableCell>
                        <TableCell>
                          {form.watch("parcels").length > 1 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                const parcels = form.getValues("parcels");
                                form.setValue(
                                  "parcels",
                                  parcels.filter((_, i) => i !== index)
                                );
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
              <CardFooter className="justify-center border-t p-4">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="gap-1"
                  onClick={() => {
                    const parcels = form.getValues("parcels");
                    form.setValue("parcels", [
                      ...parcels,
                      {
                        weight: 2,
                        dimensions: { length: 30, width: 30, height: 30 },
                        isFragile: false,
                        requiresCooling: false,
                        requiresWarm: false,
                        isPriority: false,
                        requireSignature: false,
                        extraInsurance: false,
                        leaveAtDoor: false,
                        value: 0,
                      },
                    ]);
                  }}
                >
                  <PlusCircle className="h-3.5 w-3.5" />
                  Add Parcel
                </Button>
              </CardFooter>
            </Card>

            {/* Update the Pricing Options and Map Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-8">
              {/* Map Column */}
              <Card className="h-[600px]">
                <CardContent className="p-0 h-full">
                  {!!form.watch("pickupAddress.location")?.lat &&
                  !!form.watch("deliveryAddress.location")?.lat ? (
                    <ShipmentMap
                      pickupLocation={form.watch("pickupAddress.location")}
                      deliveryLocation={form.watch("deliveryAddress.location")}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <p>Enter pickup and delivery addresses</p>
                        <p className="text-sm">to view the shipment route</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pricing Options Column */}
              <Card>
                <CardHeader>
                  <CardTitle>Delivery Options</CardTitle>
                </CardHeader>
                <CardContent>
                  {pricingOptions.length > 0 ? (
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <div className="space-y-4">
                          {pricingOptions.map((option) => (
                            <div
                              key={option.speedType}
                              className={`p-6 rounded-xl max-w-[500px] border-2 cursor-pointer transition-all ${
                                field.value === option.speedType
                                  ? "border-blue-500 bg-blue-50/50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              onClick={() =>
                                field.onChange(
                                  option.speedType as
                                    | "STANDARD"
                                    | "EXPRESS"
                                    | "SCHEDULED"
                                    | "SAME_DAY"
                                )
                              }
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-medium text-lg">
                                    {option.speedType.replace("_", " ")}
                                  </h4>
                                  <p className="text-sm text-gray-500 mt-1">
                                    {option.speedDescription}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium">
                                    ${option.price.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    />
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500">
                        {shippingOptionsError ??
                          "Complete the form to view delivery options"}
                      </p>
                      {shippingOptionsError && (
                        <p className="text-sm text-amber-600 mt-2">
                          {shippingOptionsError}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <DockedContainer isInDialog={true}>
            <div className="lg:max-w-7xl mx-auto px-4 lg:px-8">
              <Button
                type="submit"
                className="w-[300px] text-lg py-6 bg-white text-black hover:bg-white/90"
                disabled={createOrderMutation.isPending}
              >
                {createOrderMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {createOrderMutation.isPending
                  ? "Creating Order..."
                  : "Create Order"}
              </Button>
            </div>
          </DockedContainer>
        </form>
      </Form>
    </div>
  );
}
